/**
 * A quem o vínculo aponta.
 *
 * Fora da plataforma o servidor só aceita a entidade do contexto ativo, então não há o que
 * perguntar: o campo mostra a entidade e devolve o valor sozinho. Na plataforma a escolha é
 * real, e a busca é por nome — o identificador nunca chega a quem usa.
 */

import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Business as BusinessIcon,
  MedicalServices as ProviderIcon,
} from '@mui/icons-material';

import { companyService } from '../../../services/companyService';
import { providerService } from '../../../services/providerService';
import { BindingEntityType } from '../../../types/user';
import type { EntidadeDoVinculo } from '../../../hooks/useBindingScope';

export interface BindingEntityValue {
  entityType: BindingEntityType | '';
  entityId: string;
}

interface OpcaoDeEntidade {
  id: number;
  nome: string;
  detalhe?: string;
}

interface BindingEntityFieldProps {
  value: BindingEntityValue;
  onChange: (value: BindingEntityValue) => void;
  /** Fora da plataforma: a entidade já determinada pelo contexto ativo. */
  entidadeFixa?: EntidadeDoVinculo;
  disabled?: boolean;
}

const ROTULO_DA_ENTIDADE: Record<BindingEntityType, string> = {
  [BindingEntityType.COMPANY]: 'Empresa',
  [BindingEntityType.PROVIDER]: 'Clínica',
};

const ROTULO_GENERICO: Record<BindingEntityType, string> = {
  [BindingEntityType.COMPANY]: 'A empresa em que você está operando',
  [BindingEntityType.PROVIDER]: 'A clínica em que você está operando',
};

const MINIMO_PARA_BUSCAR = 2;

async function buscarEntidades(
  tipo: BindingEntityType,
  termo: string
): Promise<OpcaoDeEntidade[]> {
  if (tipo === BindingEntityType.COMPANY) {
    const resposta = await companyService.getCompanies({ search: termo, limit: 20 });
    return resposta.data.map((empresa) => ({
      id: Number(empresa.id),
      nome: empresa.tradeName || empresa.legalName,
      detalhe: empresa.cnpj,
    }));
  }

  const resposta = await providerService.getProviders({ search: termo, limit: 20 });
  return resposta.data.map((prestador) => ({
    id: Number(prestador.id),
    nome: prestador.tradeName || prestador.legalName,
    detalhe: prestador.cnpj ?? undefined,
  }));
}

/** A entidade determinada pelo contexto: mostrada, nunca perguntada. */
const EntidadeFixada: React.FC<{ entidade: EntidadeDoVinculo }> = ({ entidade }) => {
  const Icone = entidade.entityType === BindingEntityType.COMPANY ? BusinessIcon : ProviderIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        p: 1.5,
        borderRadius: 1,
        borderLeft: 3,
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
      }}
    >
      <Icone color="primary" fontSize="small" sx={{ mt: 0.3 }} />
      <Box>
        <Typography
          variant="caption"
          color="primary"
          fontWeight={700}
          sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
          display="block"
        >
          {ROTULO_DA_ENTIDADE[entidade.entityType]}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700}>
          {entidade.nome || ROTULO_GENERICO[entidade.entityType]}
        </Typography>
        {entidade.detalhe && (
          <Typography variant="caption" color="text.secondary">
            {entidade.detalhe}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const BindingEntityField: React.FC<BindingEntityFieldProps> = ({
  value,
  onChange,
  entidadeFixa,
  disabled = false,
}) => {
  const [termo, setTermo] = useState('');
  const [opcoes, setOpcoes] = useState<OpcaoDeEntidade[]>([]);
  const [selecionada, setSelecionada] = useState<OpcaoDeEntidade | null>(null);
  const [buscando, setBuscando] = useState(false);

  const tipoEscolhido = value.entityType || BindingEntityType.COMPANY;

  const termoBuscavel = termo.trim().length >= MINIMO_PARA_BUSCAR;
  // Termo curto não zera a lista guardada: basta não exibi-la.
  const opcoesVisiveis = termoBuscavel ? opcoes : [];

  useEffect(() => {
    if (entidadeFixa || !termoBuscavel) return;

    let ativo = true;
    const timer = setTimeout(() => {
      setBuscando(true);
      buscarEntidades(tipoEscolhido, termo.trim())
        .then((encontradas) => {
          if (ativo) setOpcoes(encontradas);
        })
        .catch(() => {
          if (ativo) setOpcoes([]);
        })
        .finally(() => {
          if (ativo) setBuscando(false);
        });
    }, 300);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [termo, termoBuscavel, tipoEscolhido, entidadeFixa]);

  if (entidadeFixa) {
    return <EntidadeFixada entidade={entidadeFixa} />;
  }

  const trocarTipo = (tipo: BindingEntityType | null) => {
    if (!tipo || tipo === value.entityType) return;
    setSelecionada(null);
    setOpcoes([]);
    setTermo('');
    onChange({ entityType: tipo, entityId: '' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={tipoEscolhido}
        onChange={(_, tipo: BindingEntityType | null) => trocarTipo(tipo)}
        disabled={disabled}
        aria-label="A quem a pessoa pertence"
      >
        <ToggleButton value={BindingEntityType.COMPANY}>Empresa</ToggleButton>
        <ToggleButton value={BindingEntityType.PROVIDER}>Clínica</ToggleButton>
      </ToggleButtonGroup>

      <Autocomplete
        options={opcoesVisiveis}
        value={selecionada}
        loading={buscando}
        disabled={disabled}
        filterOptions={(opcoesRecebidas) => opcoesRecebidas}
        getOptionLabel={(opcao: OpcaoDeEntidade) => opcao.nome}
        isOptionEqualToValue={(opcao, escolhida) => opcao.id === escolhida.id}
        onInputChange={(_, texto) => setTermo(texto)}
        onChange={(_, opcao: OpcaoDeEntidade | null) => {
          setSelecionada(opcao);
          onChange({ entityType: tipoEscolhido, entityId: opcao ? String(opcao.id) : '' });
        }}
        noOptionsText={
          termoBuscavel
            ? `Nenhum resultado para "${termo.trim()}"`
            : 'Digite ao menos 2 caracteres do nome ou do CNPJ'
        }
        renderOption={(props, opcao: OpcaoDeEntidade) => (
          <Box component="li" {...props} key={opcao.id}>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {opcao.nome}
              </Typography>
              {opcao.detalhe && (
                <Typography variant="caption" color="text.secondary">
                  {opcao.detalhe}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={ROTULO_DA_ENTIDADE[tipoEscolhido]}
            required
            placeholder="Busque pelo nome ou CNPJ"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {buscando ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default BindingEntityField;
